import { Link, router } from '@inertiajs/react'
import { useEffect, useRef, useState } from 'react'
import InfoTip from '../../components/InfoTip'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatAt(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function timeAgo(iso) {
  if (!iso) return null
  const sec = Math.round((Date.now() - new Date(iso).getTime()) / 1000)
  if (sec < 5)  return 'just now'
  if (sec < 60) return `${sec}s ago`
  return `${Math.round(sec / 60)}m ago`
}

const NETWORK_TYPE_LABEL = {
  standard_psk:   { label: 'Guest PSK',       color: '#9E9E9E', tier: 'T0' },
  ipsk_no_radius: { label: 'iPSK (Guest)',     color: '#2196F3', tier: 'T1' },
  ipsk_radius:    { label: 'iPSK (Standard)',  color: '#4CAF50', tier: 'T1+' },
  trusted_access: { label: 'Trusted Access',   color: '#9C27B0', tier: 'T2' },
}

const MAC_STRATEGY_STYLE = {
  stable:     { label: 'Stable MAC',     color: '#4CAF50' },
  randomized: { label: 'Randomized MAC', color: '#FF9800' },
  unknown:    { label: 'MAC Unknown',    color: '#9E9E9E' },
}

const IDENTITY_LABEL = {
  did_browser:  { label: 'DID:Browser',  color: '#9E9E9E' },
  did_guest:    { label: 'DID:Guest',    color: '#2196F3' },
  did_standard: { label: 'DID:Standard', color: '#4CAF50' },
}

function Badge({ label, color, small }) {
  return (
    <span
      style={{ backgroundColor: color + '22', color, borderColor: color }}
      className={`inline-flex items-center px-2 rounded-full border font-bold ${small ? 'text-xs py-0' : 'text-xs py-0.5'}`}
    >
      <span style={{ backgroundColor: color }} className="w-1.5 h-1.5 rounded-full mr-1" />
      {label}
    </span>
  )
}

// ─── Floor Plan ───────────────────────────────────────────────────────────────
// Renders an expo hall overhead view with:
//   - 4-corner AP placement for RSSI trilateration
//   - Booth outlines (3×5 grid)
//   - Live guest position dots with clickable infowindow
//   - RSSI signal rings drawn from each AP toward located guests

function FloorPlan({ accessPoints, sessions }) {
  const W = 480, H = 300
  const [activeInfo, setActiveInfo] = useState(null)   // { x, y, session }

  if (!accessPoints.length) return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 text-center text-sm text-gray-400">
      No APs mapped yet. Run <code>rails db:seed</code> to create demo profiles.
    </div>
  )

  // For 4-corner APs the bounding box IS the floor space.
  // We project so corners land exactly at the canvas corners (with a small margin).
  const MARGIN = 28
  const lats = accessPoints.map(a => a.lat)
  const lngs = accessPoints.map(a => a.lng)
  const minLat = Math.min(...lats), maxLat = Math.max(...lats)
  const minLng = Math.min(...lngs), maxLng = Math.max(...lngs)

  function project(lat, lng) {
    const latRange = (maxLat - minLat) || 0.0001
    const lngRange = (maxLng - minLng) || 0.0001
    const x = MARGIN + ((lng - minLng) / lngRange) * (W - MARGIN * 2)
    const y = MARGIN + (1 - (lat - minLat) / latRange) * (H - MARGIN * 2)
    return { x: Math.round(x), y: Math.round(y) }
  }

  const locatedDots = sessions
    .filter(s => s.indoor_position)
    .map(s => ({
      ...project(s.indoor_position.lat, s.indoor_position.lng),
      session: s,
      rssi:    s.indoor_position.rssi,
      seenAt:  s.indoor_position.seen_at,
      color:   NETWORK_TYPE_LABEL[s.network_type]?.color || '#2196F3',
    }))

  // Booth grid — 3 rows × 5 cols inside the hall space
  const hallX = MARGIN + 2, hallY = MARGIN + 2
  const hallW = W - MARGIN * 2 - 4, hallH = H - MARGIN * 2 - 4
  const COLS = 5, ROWS = 3, AISLE_H = 14
  const totalBoothH = (hallH - AISLE_H * (ROWS - 1))
  const boothW = (hallW - 8) / COLS - 4
  const boothH = totalBoothH / ROWS - 2
  const BOOTH_LABELS = ['A1','A2','A3','A4','A5','B1','B2','B3','B4','B5','C1','C2','C3','C4','C5']

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 relative">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
        Indoor Floor Plan — Expo Hall · 4-corner AP trilateration (live)
      </p>
      <svg
        width={W} height={H}
        className="rounded border border-gray-200 w-full"
        style={{ backgroundColor: '#F5F7FA', maxWidth: W }}
        onClick={() => setActiveInfo(null)}
      >
        {/* Outer hall boundary */}
        <rect x={6} y={6} width={W - 12} height={H - 12} rx={8}
              fill="#FFFFFF" stroke="#CFD8DC" strokeWidth={1.5} />

        {/* Booth rows */}
        {Array.from({ length: ROWS }).map((_, ri) => {
          const rowY = hallY + 4 + ri * (boothH + AISLE_H)
          return Array.from({ length: COLS }).map((_, ci) => {
            const bx = hallX + 4 + ci * (boothW + 4)
            const label = BOOTH_LABELS[ri * COLS + ci]
            return (
              <g key={`b${ri}${ci}`}>
                <rect x={bx} y={rowY} width={boothW} height={boothH}
                      fill="#EEF2F7" stroke="#B0BEC5" strokeWidth={0.8} rx={2} />
                <text x={bx + boothW / 2} y={rowY + boothH / 2 + 3}
                      textAnchor="middle" fontSize={8} fill="#78909C" fontWeight="600">{label}</text>
              </g>
            )
          })
        })}

        {/* Aisle labels */}
        {[1].map(ri => (
          <text key={`aisle${ri}`}
                x={W / 2}
                y={hallY + 4 + ri * (boothH + AISLE_H) - AISLE_H / 2 + 4}
                textAnchor="middle" fontSize={7} fill="#B0BEC5">— MAIN AISLE —</text>
        ))}

        {/* Entrance */}
        <text x={W / 2} y={H - 8} textAnchor="middle" fontSize={8} fill="#90A4AE" fontStyle="italic">
          ↑ Entrance / Exit
        </text>

        {/* RSSI signal rings from each AP toward located guests */}
        {accessPoints.map(ap => {
          const apPos = project(ap.lat, ap.lng)
          return locatedDots.map((dot, di) => {
            const dist = Math.sqrt((apPos.x - dot.x) ** 2 + (apPos.y - dot.y) ** 2)
            return (
              <circle key={`ring-${ap.bssid}-${di}`}
                      cx={apPos.x} cy={apPos.y} r={dist}
                      fill="none" stroke="#2196F330" strokeWidth={0.8} strokeDasharray="3 4" />
            )
          })
        })}

        {/* AP icons — pinned to corners */}
        {accessPoints.map(ap => {
          const { x, y } = project(ap.lat, ap.lng)
          const corner = ap.floor_label?.includes('NW') ? 'NW'
                       : ap.floor_label?.includes('NE') ? 'NE'
                       : ap.floor_label?.includes('SW') ? 'SW'
                       : ap.floor_label?.includes('SE') ? 'SE' : ''
          return (
            <g key={ap.bssid}>
              <circle cx={x} cy={y} r={11} fill="#1565C033" stroke="#1565C0" strokeWidth={1.5} />
              <text x={x} y={y + 4} textAnchor="middle" fontSize={10}>📡</text>
              <text x={x} y={y + (corner === 'SW' || corner === 'SE' ? -14 : 20)}
                    textAnchor="middle" fontSize={7} fill="#1565C0" fontWeight="700">
                {corner || ap.bssid.slice(-5)}
              </text>
            </g>
          )
        })}

        {/* Guest position dots — clickable */}
        {locatedDots.map((dot, i) => (
          <g key={i} style={{ cursor: 'pointer' }}
             onClick={e => { e.stopPropagation(); setActiveInfo({ x: dot.x, y: dot.y, session: dot.session, rssi: dot.rssi }) }}>
            {/* Outer pulse ring */}
            <circle cx={dot.x} cy={dot.y} r={16} fill={dot.color + '25'} />
            {/* Main dot */}
            <circle cx={dot.x} cy={dot.y} r={8} fill={dot.color} stroke="#fff" strokeWidth={1.5} />
            {/* Guest initial */}
            <text x={dot.x} y={dot.y + 4} textAnchor="middle" fontSize={8} fill="#fff" fontWeight="bold">
              {dot.session.guest_name?.[0]?.toUpperCase() || '?'}
            </text>
            {/* Name label */}
            <text x={dot.x} y={dot.y - 20} textAnchor="middle" fontSize={8} fill={dot.color} fontWeight="600">
              {dot.session.guest_name?.split(' ')[0] || 'Guest'}
            </text>
            {dot.rssi && (
              <text x={dot.x} y={dot.y + 22} textAnchor="middle" fontSize={7} fill="#78909C">
                {dot.rssi} dBm
              </text>
            )}
          </g>
        ))}

        {/* Infowindow for selected dot */}
        {activeInfo && (() => {
          const iw = activeInfo
          const nt = NETWORK_TYPE_LABEL[iw.session.network_type] || { label: iw.session.network_type, color: '#9E9E9E', tier: '?' }
          // Position infowindow so it doesn't clip the SVG edge
          const iwW = 160, iwH = 90
          const ix = Math.min(iw.x + 16, W - iwW - 8)
          const iy = Math.min(iw.y - 20, H - iwH - 8)
          return (
            <g>
              <rect x={ix} y={iy} width={iwW} height={iwH} rx={6}
                    fill="#1A1A2E" stroke="#E94560" strokeWidth={1.5}
                    filter="url(#shadow)" />
              <text x={ix + 8} y={iy + 16} fontSize={9} fill="#E94560" fontWeight="700">
                {iw.session.guest_name}
              </text>
              <text x={ix + 8} y={iy + 28} fontSize={8} fill="#A0A0C0">
                {iw.session.offer_name?.slice(0, 22) || 'Unknown offer'}
              </text>
              <text x={ix + 8} y={iy + 40} fontSize={7} fill="#607D8B">
                PS #{iw.session.id} · {nt.tier} {nt.label}
              </text>
              <text x={ix + 8} y={iy + 52} fontSize={7} fill="#607D8B">
                Device: {iw.session.guest_device_id?.slice(-12) || '—'}
              </text>
              <text x={ix + 8} y={iy + 64} fontSize={7} fill="#607D8B">
                MAC: {iw.session.guest_mac_address || '—'} ({iw.session.guest_mac_strategy || '?'})
              </text>
              <text x={ix + 8} y={iy + 76} fontSize={7} fill={iw.rssi ? '#4CAF50' : '#607D8B'}>
                RSSI: {iw.rssi ? `${iw.rssi} dBm` : 'no signal data'}
              </text>
            </g>
          )
        })()}

        {/* Drop-shadow filter */}
        <defs>
          <filter id="shadow" x="-10%" y="-10%" width="120%" height="120%">
            <feDropShadow dx="2" dy="2" stdDeviation="3" floodOpacity="0.3" />
          </filter>
        </defs>
      </svg>
      <p className="text-xs text-gray-400 mt-1">
        📡 APs at 4 corners (RSSI trilateration) · blue dots = located guests · click a dot for details
        {locatedDots.length === 0 && ' — use Simulate Scan below to place a guest dot'}
      </p>
    </div>
  )
}

// ─── Simulate Scan panel ──────────────────────────────────────────────────────

function SimulateScanPanel({ sessions, accessPoints }) {
  const [mac, setMac]         = useState(sessions[0]?.guest_mac_address || '00:00:00:de:mo:01')
  const [lat, setLat]         = useState(accessPoints[0]?.lat?.toFixed(5) || '')
  const [lng, setLng]         = useState(accessPoints[0]?.lng?.toFixed(5) || '')
  const [rssi, setRssi]       = useState('-65')
  const [sending, setSending] = useState(false)
  const [result, setResult]   = useState(null)
  const [error, setError]     = useState(null)

  function pickAp(ap) {
    setLat(ap.lat.toFixed(5))
    setLng(ap.lng.toFixed(5))
  }

  // Place dot at center of the floor space (midpoint of AP bounding box)
  function pickCenter() {
    if (!accessPoints.length) return
    const midLat = (Math.min(...accessPoints.map(a => a.lat)) + Math.max(...accessPoints.map(a => a.lat))) / 2
    const midLng = (Math.min(...accessPoints.map(a => a.lng)) + Math.max(...accessPoints.map(a => a.lng))) / 2
    setLat(midLat.toFixed(5))
    setLng(midLng.toFixed(5))
  }

  function send() {
    if (sending) return
    setSending(true)
    setError(null)
    setResult(null)
    router.post('/admin/network_monitor/simulate_scan',
      { mac_address: mac, lat: parseFloat(lat), lng: parseFloat(lng), rssi: parseInt(rssi) },
      {
        preserveScroll: true,
        onSuccess: () => {
          setResult({ mac, lat: parseFloat(lat), lng: parseFloat(lng), rssi: parseInt(rssi) })
          setSending(false)
        },
        onError: (errs) => {
          setError(JSON.stringify(errs))
          setSending(false)
        },
      }
    )
  }

  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
      <p className="text-xs font-semibold text-yellow-800 mb-2">
        🛰 Simulate Meraki DevicesSeen Scan (demo)
      </p>

      {/* Quick-pick buttons */}
      <div className="flex gap-1 flex-wrap mb-2">
        <span className="text-xs text-gray-400 self-center mr-1">Jump to:</span>
        <button onClick={pickCenter}
                className="text-xs border border-green-400 text-green-700 px-2 py-0.5 rounded hover:bg-green-50">
          ⊕ Center
        </button>
        {accessPoints.map(ap => {
          const corner = ap.floor_label?.match(/N[WE]|S[WE]/)?.[0] || ap.bssid.slice(-5)
          return (
            <button key={ap.bssid} onClick={() => pickAp(ap)}
                    className="text-xs border border-blue-300 text-blue-600 px-2 py-0.5 rounded hover:bg-blue-50">
              AP-{corner}
            </button>
          )
        })}
        {sessions[0] && (
          <button onClick={() => setMac(sessions[0].guest_mac_address || mac)}
                  className="text-xs border border-purple-300 text-purple-600 px-2 py-0.5 rounded hover:bg-purple-50">
            Use Session MAC
          </button>
        )}
      </div>

      <div className="grid grid-cols-5 gap-2">
        <div>
          <label className="block text-xs text-gray-500 mb-1">MAC Address</label>
          <input value={mac} onChange={e => setMac(e.target.value)}
                 className="w-full border rounded px-2 py-1 text-xs font-mono" placeholder="aa:bb:cc:..." />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Latitude</label>
          <input value={lat} onChange={e => setLat(e.target.value)}
                 className="w-full border rounded px-2 py-1 text-xs" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Longitude</label>
          <input value={lng} onChange={e => setLng(e.target.value)}
                 className="w-full border rounded px-2 py-1 text-xs" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">RSSI (dBm)</label>
          <input value={rssi} onChange={e => setRssi(e.target.value)}
                 className="w-full border rounded px-2 py-1 text-xs" />
        </div>
        <div className="flex items-end">
          <button onClick={send} disabled={sending}
                  className="w-full bg-yellow-600 text-white text-xs px-3 py-1.5 rounded hover:bg-yellow-700 disabled:opacity-50">
            {sending ? '…' : 'Send Scan'}
          </button>
        </div>
      </div>
      {result && (
        <p className="text-xs text-green-700 mt-2">
          ✓ {result.mac} placed @ {result.lat}, {result.lng} ({result.rssi} dBm) — dot appears on floor plan
        </p>
      )}
      {error && <p className="text-xs text-red-600 mt-2">✗ {error}</p>}
    </div>
  )
}

// ─── Chat window ──────────────────────────────────────────────────────────────

// queueSessionId — QueueSession ID for polling messages (API endpoint)
// propertySessionId — PropertySession ID for sending operator messages (admin endpoint)
// height — optional CSS height string (default '100%' — fills parent flex container)
function ChatWindow({ queueSessionId, propertySessionId, height = '100%' }) {
  const [messages, setMessages] = useState([])
  const [text, setText]         = useState('')
  const [sending, setSending]   = useState(false)
  const bottomRef               = useRef(null)

  useEffect(() => {
    if (!queueSessionId) return
    fetchMessages()
    const iv = setInterval(fetchMessages, 5000)
    return () => clearInterval(iv)
  }, [queueSessionId]) // eslint-disable-line react-hooks/exhaustive-deps

  function fetchMessages() {
    if (!queueSessionId) return
    // Use the admin proxy endpoint (/admin/network_monitor/session_messages) which
    // accepts the web cookie session. The Bearer-token API endpoint (/api/v1/unified_api/...)
    // is not accessible from the admin web UI — it requires a mobile auth token.
    fetch(`/admin/network_monitor/session_messages?queue_session_id=${queueSessionId}`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return
        const chatMsgs = (data.messages || [])
          .filter(m => m.message_type === 'operator_chat' || m.message_type === 'guest_chat')
          .map(m => ({
            id:   m.id,
            from: m.message_type === 'operator_chat' ? 'operator' : 'guest',
            text: m.payload?.text || '',
            at:   m.created_at,
          }))
        setMessages(chatMsgs)
        setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50)
      })
      .catch(() => {})
  }

  function sendMessage() {
    if (!text.trim() || !propertySessionId || sending) return
    setSending(true)
    router.post('/admin/network_monitor/operator_message',
      { property_session_id: propertySessionId, text: text.trim() },
      {
        preserveScroll: true,
        onSuccess: () => { setText(''); setSending(false); fetchMessages() },
        onError:   () => setSending(false),
      }
    )
  }

  if (!queueSessionId) return (
    <div className="flex flex-col border border-gray-200 rounded-lg overflow-hidden" style={{ height }}>
      <div className="bg-blue-50 border-b border-blue-100 px-3 py-2">
        <p className="text-xs font-semibold text-blue-700">💬 Guest Chat</p>
      </div>
      <div className="flex-1 flex items-center justify-center p-3">
        <p className="text-xs text-gray-400 text-center">No active queue session linked —<br/>chat available once guest connects</p>
      </div>
    </div>
  )

  return (
    <div className="flex flex-col border border-blue-200 rounded-lg overflow-hidden" style={{ height }}>
      {/* Header */}
      <div className="bg-blue-50 border-b border-blue-100 px-3 py-2 flex items-center justify-between flex-shrink-0">
        <p className="text-xs font-semibold text-blue-700">💬 Guest Chat</p>
        <span className="text-xs text-gray-400">Session #{queueSessionId}</span>
      </div>
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-2 space-y-1 bg-white min-h-0">
        {messages.length === 0 && (
          <p className="text-xs text-gray-400 text-center pt-4">No messages yet</p>
        )}
        {messages.map(m => (
          <div key={m.id} className={`flex ${m.from === 'operator' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] px-2 py-1 rounded text-xs ${m.from === 'operator' ? 'bg-blue-600 text-white' : 'bg-gray-100 border border-gray-200 text-gray-800'}`}>
              {m.text}
              <span className={`block text-xs mt-0.5 ${m.from === 'operator' ? 'text-blue-200' : 'text-gray-400'}`}>
                {m.from === 'operator' ? 'You' : 'Guest'} · {timeAgo(m.at)}
              </span>
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
      {/* Input */}
      <div className="flex border-t border-gray-200 overflow-hidden flex-shrink-0">
        <input
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && sendMessage()}
          placeholder="Message guest…"
          className="flex-1 px-2 py-1.5 text-xs outline-none"
        />
        <button onClick={sendMessage} disabled={sending || !text.trim()}
                className="bg-blue-600 text-white text-xs px-3 hover:bg-blue-700 disabled:opacity-40 flex-shrink-0">
          {sending ? '…' : 'Send'}
        </button>
      </div>
    </div>
  )
}

// ─── Session card ──────────────────────────────────────────────────────────────
// Two-column layout when expanded:
//   Left (2/3 width)  — Device Identity, Lifecycle, Indoor Position, Metadata panels
//   Right (1/3 width) — Always-visible Chat panel, spans full height of activity panels

function SessionCard({ session, isHistory = false }) {
  const [expanded, setExpanded]   = useState(false)
  const [closing, setClosing]     = useState(false)
  const nt   = NETWORK_TYPE_LABEL[session.network_type]  || { label: session.network_type || '—',  color: '#9E9E9E', tier: '?' }
  const ms   = MAC_STRATEGY_STYLE[session.guest_mac_strategy] || MAC_STRATEGY_STYLE.unknown
  const id_  = IDENTITY_LABEL[session.identity_mode]     || { label: session.identity_mode || '—', color: '#9E9E9E' }
  const pos  = session.indoor_position
  const statusColor = session.session_status === 'active' ? '#4CAF50' : '#FF9800'

  function closeSession(e) {
    e.stopPropagation()
    if (closing) return
    if (!window.confirm(`Close PS #${session.id} (${session.guest_name})? This ends the network session.`)) return
    setClosing(true)
    router.post('/admin/network_monitor/close_session',
      { property_session_id: session.id },
      {
        preserveScroll: true,
        onFinish: () => setClosing(false),
      }
    )
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      {/* Header — always visible, click to expand/collapse activity panels */}
      <div className="px-4 py-3 flex items-center justify-between hover:bg-gray-50">
        <button
          className="flex items-center gap-3 min-w-0 flex-1 text-left"
          onClick={() => setExpanded(e => !e)}
        >
          <div style={{ backgroundColor: statusColor }} className="w-2.5 h-2.5 rounded-full flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">
              {session.guest_name}
              <span className="ml-2 text-xs text-gray-400 font-normal">PS #{session.id}</span>
            </p>
            <p className="text-xs text-gray-500 truncate">{session.offer_name}</p>
          </div>
        </button>
        <div className="flex items-center gap-2 flex-shrink-0 ml-3">
          <Badge label={nt.tier + ' · ' + nt.label} color={nt.color} small />
          <Badge label={id_.label} color={id_.color} small />
          <Badge label={ms.label} color={ms.color} small />
          {!isHistory && (
            <button
              onClick={closeSession}
              disabled={closing}
              className="text-xs px-2 py-0.5 rounded border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-40 ml-1"
            >
              {closing ? '…' : 'Close'}
            </button>
          )}
          <button onClick={() => setExpanded(e => !e)} className="text-gray-400 text-sm ml-1">
            {expanded ? '▲' : '▼'}
          </button>
        </div>
      </div>

      {/* ── Two-column body: activity panels left, chat right ── */}
      {expanded && (
        <div className="border-t border-gray-100 flex" style={{ minHeight: 320 }}>

          {/* Left column — activity panels (2/3 width) */}
          <div className="flex-1 px-4 py-3 space-y-3 text-xs border-r border-gray-100 overflow-y-auto">

            {/* Device Identity & PSK Provisioning */}
            <div>
              <p className="text-gray-400 font-semibold uppercase tracking-wide mb-1.5">Device Identity & PSK Provisioning</p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                <div>
                  <span className="text-gray-400">Device ID (anchor)</span>
                  <p className="font-mono text-gray-700 break-all text-xs">{session.guest_device_id || '—'}</p>
                </div>
                <div>
                  <span className="text-gray-400">Identity Mode</span>
                  <p style={{ color: id_.color }} className="font-semibold">{id_.label}</p>
                </div>
                <div>
                  <span className="text-gray-400">MAC Address</span>
                  <p className="font-mono text-gray-700">{session.guest_mac_address || '—'}</p>
                </div>
                <div>
                  <span className="text-gray-400">MAC Strategy (ADR-009)</span>
                  <p style={{ color: ms.color }} className="font-semibold">{ms.label}</p>
                </div>
                <div>
                  <span className="text-gray-400">PSK Tier</span>
                  <p style={{ color: nt.color }} className="font-semibold">{nt.label} ({nt.tier})</p>
                </div>
                <div>
                  <span className="text-gray-400">Network / SSID</span>
                  <p className="text-gray-700 font-mono">{session.network_ssid || '—'}</p>
                </div>
                <div>
                  <span className="text-gray-400">Meraki Network ID</span>
                  <p className="font-mono text-gray-700">{session.meraki_network_id || '—'}</p>
                </div>
                <div>
                  <span className="text-gray-400">iPSK Credential ID</span>
                  <p className="font-mono text-gray-700">{session.meraki_ipsk_id || '(demo — no Meraki API)'}</p>
                </div>
                <div>
                  <span className="text-gray-400">PSK (masked)</span>
                  <p className="font-mono text-gray-700">{session.network_credential_masked || '—'}</p>
                </div>
                <div>
                  <span className="text-gray-400">Network Type</span>
                  <a href="/admin/network_monitor"
                     className="font-semibold hover:underline" style={{ color: nt.color }}>
                    {nt.label} ↗
                  </a>
                </div>
              </div>
            </div>

            {/* Session Lifecycle */}
            <div>
              <p className="text-gray-400 font-semibold uppercase tracking-wide mb-1.5">Session Lifecycle</p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                <div>
                  <span className="text-gray-400">Status</span>
                  <p style={{ color: statusColor }} className="font-semibold capitalize">{session.session_status}</p>
                </div>
                <div>
                  <span className="text-gray-400">Activated</span>
                  <p className="text-gray-700">{formatAt(session.activated_at)}</p>
                </div>
                <div>
                  <span className="text-gray-400">Scheduled Checkout</span>
                  <p className="text-gray-700">{formatAt(session.scheduled_checkout_at)}</p>
                </div>
                <div>
                  <span className="text-gray-400">Closed</span>
                  <p className="text-gray-700">{formatAt(session.closed_at)}</p>
                </div>
                {session.queue_session_id && (
                  <div>
                    <span className="text-gray-400">Queue Session</span>
                    <p className="font-mono text-gray-700">#{session.queue_session_id}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Indoor Position */}
            <div>
              <p className="text-gray-400 font-semibold uppercase tracking-wide mb-1.5">Indoor Position (Meraki RSSI)</p>
              {pos ? (
                <div className="grid grid-cols-3 gap-x-4 gap-y-1">
                  <div><span className="text-gray-400">Lat / Lng</span><p className="font-mono text-gray-700">{pos.lat.toFixed(6)}, {pos.lng.toFixed(6)}</p></div>
                  <div><span className="text-gray-400">RSSI</span><p className="font-mono text-gray-700">{pos.rssi ? `${pos.rssi} dBm` : '—'}</p></div>
                  <div><span className="text-gray-400">Seen</span><p className="text-gray-700">{timeAgo(pos.seen_at)}</p></div>
                </div>
              ) : (
                <p className="text-gray-400 italic">Not in Meraki cache — use Simulate Scan to place dot</p>
              )}
            </div>

            {/* Network Metadata */}
            {session.network_metadata && Object.keys(session.network_metadata).length > 0 && (
              <div>
                <p className="text-gray-400 font-semibold uppercase tracking-wide mb-1">Network Metadata</p>
                <pre className="bg-gray-50 rounded p-2 text-xs text-gray-600 overflow-x-auto">
                  {JSON.stringify(session.network_metadata, null, 2)}
                </pre>
              </div>
            )}

            {/* Linked Queue Sessions — arrival queue + all service queues under this PS */}
            <div>
              <p className="text-gray-400 font-semibold uppercase tracking-wide mb-1.5">
                Linked Queue Sessions
                {session.linked_queue_sessions?.length > 0 && (
                  <span className="ml-1 text-gray-300 font-normal normal-case">({session.linked_queue_sessions.length})</span>
                )}
              </p>
              {!session.linked_queue_sessions?.length ? (
                <p className="text-gray-400 italic">No queue sessions linked yet</p>
              ) : (
                <div className="space-y-1">
                  {session.linked_queue_sessions.map(qs => (
                    <div key={qs.id} className="flex items-center gap-2 bg-gray-50 rounded px-2 py-1">
                      <a
                        href={`/admin/queue_monitor/${qs.id}`}
                        className="font-mono text-blue-600 hover:underline font-semibold text-xs"
                        title="Open in Queue Monitor"
                      >
                        #{qs.id}
                      </a>
                      <span className="flex-1 text-gray-600 truncate">{qs.offer_name || 'Unknown offer'}</span>
                      <span className={`px-1.5 rounded text-xs font-medium ${
                        qs.session_status === 'active'   ? 'bg-green-100 text-green-700' :
                        qs.session_status === 'closed'   ? 'bg-gray-100 text-gray-500'   :
                        qs.session_status === 'cancelled'? 'bg-red-100 text-red-500'     :
                                                           'bg-blue-100 text-blue-600'
                      }`}>{qs.session_status}</span>
                      <span className="text-gray-400">{formatAt(qs.created_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Scheduled Sessions */}
            {session.scheduled_sessions.length > 0 && (
              <div>
                <p className="text-gray-400 font-semibold uppercase tracking-wide mb-1">Scheduled Sessions</p>
                <div className="space-y-1">
                  {session.scheduled_sessions.map(s => (
                    <div key={s.id} className="flex items-center gap-2">
                      <span className="text-gray-600">{s.offer_name}</span>
                      <span className="text-gray-400">·</span>
                      <span className="text-blue-600">{new Date(s.scheduled_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}</span>
                      <span className={`px-1.5 py-0 rounded text-xs font-medium ${s.status === 'pending' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                        {s.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right column — always-visible chat (1/3 width, spans full height) */}
          <div className="flex-shrink-0 p-3" style={{ width: 280 }}>
            <ChatWindow
              queueSessionId={session.queue_session_id}
              propertySessionId={session.id}
              height="100%"
            />
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function NetworkMonitor({ sessions, closed_sessions = [], access_points, demo_offer_id }) {
  const intervalRef   = useRef(null)
  const [autoRefresh, setAutoRefresh]   = useState(true)
  const [showHistory, setShowHistory]   = useState(false)

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => {
        router.reload({ only: ['sessions', 'closed_sessions'] })
      }, 10_000)
    } else {
      clearInterval(intervalRef.current)
    }
    return () => clearInterval(intervalRef.current)
  }, [autoRefresh])

  const activeSessions  = sessions.filter(s => s.session_status === 'active')
  const pausedSessions  = sessions.filter(s => s.session_status === 'paused')
  const locatedSessions = sessions.filter(s => s.indoor_position)

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Network Monitor</h1>
            <p className="text-sm text-gray-500 mt-1">
              {activeSessions.length} active · {pausedSessions.length} paused · {locatedSessions.length} located (RSSI) · {access_points.length} APs · {closed_sessions.length} closed (48h)
            </p>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer">
              <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} className="rounded" />
              Auto-refresh (10s)
            </label>
            <button onClick={() => router.reload()}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-100">
              Refresh
            </button>
            {demo_offer_id && (
              <a href={`https://api.offertraq.com/api/offers/${demo_offer_id}`} target="_blank" rel="noreferrer"
                 className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700">
                Demo Offer #{demo_offer_id}
              </a>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Active Sessions',  value: activeSessions.length,  color: '#4CAF50' },
            { label: 'Located (RSSI)',   value: locatedSessions.length, color: '#2196F3' },
            { label: 'APs Mapped',       value: access_points.length,   color: '#9C27B0' },
            { label: 'Scheduled Next',   value: sessions.reduce((n, s) => n + s.scheduled_sessions.length, 0), color: '#FF9800' },
          ].map(stat => (
            <div key={stat.label} className="bg-white border border-gray-200 rounded-lg p-3 text-center">
              <p style={{ color: stat.color }} className="text-2xl font-bold">{stat.value}</p>
              <p className="text-xs text-gray-500 mt-0.5">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Floor Plan */}
        <div className="mb-4">
          <FloorPlan accessPoints={access_points} sessions={sessions} />
        </div>

        {/* Simulate Scan */}
        <div className="mb-4">
          <SimulateScanPanel sessions={sessions} accessPoints={access_points} />
        </div>

        {/* Session list */}
        <div className="space-y-3">
          {sessions.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
              <p className="text-gray-400 text-sm">No active PropertySessions.</p>
              <p className="text-gray-400 text-xs mt-1">
                Run the demo screen on mobile using offer #{demo_offer_id ?? '(seed first)'} to create one.
              </p>
            </div>
          ) : (
            sessions.map(s => <SessionCard key={s.id} session={s} />)
          )}
        </div>

        {/* Recent history (last 48h) — collapsed by default */}
        {closed_sessions.length > 0 && (
          <div className="mt-4">
            <button
              onClick={() => setShowHistory(h => !h)}
              className="w-full flex items-center justify-between px-4 py-2 bg-white border border-gray-200 rounded-lg text-xs text-gray-500 hover:bg-gray-50"
            >
              <span className="font-semibold text-gray-600">
                Recent History (last 48h) — {closed_sessions.length} closed session{closed_sessions.length !== 1 ? 's' : ''}
              </span>
              <span>{showHistory ? '▲ Hide' : '▼ Show'}</span>
            </button>
            {showHistory && (
              <div className="space-y-2 mt-2">
                {closed_sessions.map(s => <SessionCard key={s.id} session={s} isHistory />)}
              </div>
            )}
          </div>
        )}

        {/* Nav */}
        <div className="mt-6 flex gap-4 text-sm text-gray-500">
          <Link href="/admin/queue_monitor"    className="text-blue-600 hover:underline">← Queue Monitor</Link>
          <Link href="/admin/schedule_monitor" className="text-blue-600 hover:underline">Schedule Monitor</Link>
          <Link href="/admin/journey_monitor"  className="text-blue-600 hover:underline">Journey Monitor</Link>
        </div>
      </div>
    </div>
  )
}
